import EmblaCarousel from 'embla-carousel';

// On desktop, the carousel is only needed when there are enough videos to scroll.
// With 4 or fewer, the videos fit without it; mobile always uses the carousel.
const DESKTOP_BREAKPOINT = 768;
const CAROUSEL_DISABLE_THRESHOLD = 4;

/**
 * Initializes the Product Social Videos experience.
 *
 * Responsibilities:
 * - Creates an Embla carousel for the social video rail. On desktop the carousel
 *   is skipped when there are CAROUSEL_DISABLE_THRESHOLD or fewer videos since
 *   they fit without scrolling; mobile always carousels regardless of count.
 * - Wires previous/next controls for carousel navigation.
 * - Adds hover interactions to preview videos in-place.
 * - Opens a modal with the selected video source on click.
 */
const ProductSocialVideos = () => {
  const videos = document.querySelectorAll('.product-social-videos__video-container');
  const socialVideosElement = document.querySelector('.product-social-videos__videos');
  const nextButton = document.querySelector('.product-social-videos__control.--next');
  const prevButton = document.querySelector('.product-social-videos__control.--prev');
  let embla;
  const options = {
    loop: true,
    slidesToScroll: 1,
    resize: true,
    align: 'start',
    skipSnaps: true,
    draggable: true,
    breakpoints: {
      // Center slides on smaller screens to improve framing.
      '(max-width: 768px)': {
        align: 'center',
      },
    },
  };

  function isDesktop() {
    return window.innerWidth > DESKTOP_BREAKPOINT;
  }

  // The Liquid template does not duplicate slides when count <= CAROUSEL_DISABLE_THRESHOLD,
  // so videos.length is an accurate count of unique videos in that case.
  function shouldSkipCarousel() {
    return isDesktop() && videos.length <= CAROUSEL_DISABLE_THRESHOLD;
  }

  function initCarousel() {
    if (!socialVideosElement || embla) {
      return;
    }
    embla = EmblaCarousel(socialVideosElement, options);
  }

  function destroyCarousel() {
    if (!embla) {
      return;
    }
    embla.destroy();
    embla = null;
  }

  function setNavVisibility(visible) {
    if (nextButton) {
      nextButton.hidden = !visible;
    }
    if (prevButton) {
      prevButton.hidden = !visible;
    }
  }

  // Evaluated on init and on resize so the carousel can be torn down or
  // rebuilt as the viewport crosses the desktop breakpoint.
  function handleCarouselState() {
    if (shouldSkipCarousel()) {
      destroyCarousel();
      setNavVisibility(false);
    } else {
      initCarousel();
      setNavVisibility(true);
    }
  }

  if (socialVideosElement) {
    if (nextButton) {
      nextButton.addEventListener('click', onScrollNext);
    }

    if (prevButton) {
      prevButton.addEventListener('click', onScrollPrev);
    }

    handleCarouselState();

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleCarouselState, 150);
    });
  }

  function onScrollNext() {
    embla?.scrollNext();
  }

  function onScrollPrev() {
    embla?.scrollPrev();
  }

  function onHoverVideo(event) {
    // Play the preview while the user is hovering the card.
    const video = event.currentTarget.querySelector('video');
    if (video) {
      video.play();
    }
  }

  function onLeaveVideo(event) {
    // Pause the preview when the pointer leaves the card.
    const video = event.currentTarget.querySelector('video');
    if (video) {
      video.pause();
    }
  }

  function toggleModalPlayButton(playButton, isVisible) {
    if (!playButton) {
      return;
    }

    playButton.classList.toggle('hidden', !isVisible);
  }

  function tryPlayModalVideo(modalVideo, playButton) {
    if (!modalVideo) {
      return;
    }

    const playPromise = modalVideo.play();

    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          toggleModalPlayButton(playButton, false);
        })
        .catch(() => {
          toggleModalPlayButton(playButton, true);
        });
      return;
    }

    toggleModalPlayButton(playButton, false);
  }

  function onShowVideo(event) {
    const video = event.currentTarget.querySelector('video');
    // render product-social-video-modal-template in DOM
    const modalTemplate = document.getElementById('product-social-video-modal-template');
    if (modalTemplate) {
      const modalContent = modalTemplate.content.cloneNode(true);
      const modalVideo = modalContent.querySelector('video');
      const modalSource = modalVideo?.querySelector('source');
      const modal = modalContent.querySelector('.product-social-videos__modal');
      if (modalSource) {
        // Mirror the clicked video's current source in the modal player.
        modalSource.src = video.currentSrc || video.src;
        modalVideo.poster = video.poster;
      }
      document.body.appendChild(modalContent);
      // Disable page scroll while the modal is open.
      document.body.style.overflow = 'hidden';

      const closeButton = modal?.querySelector('.product-social-videos__modal-close');
      closeButton.addEventListener('click', () => {
        if (!modal) {
          return;
        }

        modal.remove();
        // Re-enable page scroll when the modal is closed.
        document.body.style.overflow = '';
      });

      const muteButton = modal?.querySelector('.product-social-videos__modal-mute');
      muteButton.addEventListener('click', () => {
        if (modalVideo.muted) {
          modalVideo.muted = false;
          muteButton.setAttribute('aria-label', 'Mute video');
          muteButton.querySelector('.product-social-videos__modal-mute-icon').classList.add('hidden');
          muteButton.querySelector('.product-social-videos__modal-unmute-icon').classList.remove('hidden');
        } else {
          modalVideo.muted = true;
          muteButton.setAttribute('aria-label', 'Unmute video');
          muteButton.querySelector('.product-social-videos__modal-mute-icon').classList.remove('hidden');
          muteButton.querySelector('.product-social-videos__modal-unmute-icon').classList.add('hidden');
        }
      });

      const playButton = modal?.querySelector('.product-social-videos__modal-play');
      playButton?.addEventListener('click', () => {
        tryPlayModalVideo(modalVideo, playButton);
      });

      modalVideo?.addEventListener('play', () => {
        toggleModalPlayButton(playButton, false);
      });

      modalVideo?.addEventListener('pause', () => {
        if (!modalVideo.ended) {
          toggleModalPlayButton(playButton, true);
        }
      });

      if (modalSource) {
        // Mirror the clicked video's current source in the modal player.
        modalVideo.load();
        tryPlayModalVideo(modalVideo, playButton);
      }
    }
  }

  /**
   * Initializes video event listeners based on viewport size and device type.
   * Adds mouseenter, mouseleave, and click event listeners to video elements
   * only on desktop devices (width > 768px) that don't have touch support.
   *
   * @function onVideoInit
   * @returns {void}
   */
  function onVideoInit() {
    if (!videos) {
      return;
    }
    videos.forEach((video) => {
      if (window.innerWidth > 768 && !('ontouchstart' in window)) {
        video.addEventListener('mouseenter', onHoverVideo);
        video.addEventListener('mouseleave', onLeaveVideo);
      }
      video.addEventListener('click', onShowVideo);
    });
  }

  onVideoInit();
};

export default ProductSocialVideos;
